import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Rocket, TrendingUp, Lightbulb, Clock, Users, Zap, Target, BarChart3 } from 'lucide-react';

interface LoginHeroSectionProps {
  /** 메인 슬로건 텍스트 */
  mainSlogan?: string;
  /** 서브 슬로건 텍스트 */
  subSlogan?: string;
  /** 알림 카드 표시 여부 */
  showNotificationCards?: boolean;
  /** 플로팅 아이콘 표시 여부 */
  showFloatingIcons?: boolean;
}

/**
 * 로그인 페이지 히어로 섹션 컴포넌트
 * 스타트업, 투자사, 액셀러레이터 관련 시각적 요소를 포함한 배경 섹션
 */
const LoginHeroSection: React.FC<LoginHeroSectionProps> = ({
  mainSlogan = '척하면 착, 남다른 흐름',
  subSlogan = '스타트업의 A부터 Z까지',
  showNotificationCards = true,
  showFloatingIcons = true,
}) => {
  return (
    <HeroContainer>
      {/* Background Layers */}
      <BgBase />
      <BgMesh />
      <BgNoise />
      
      {/* Glow Orbs */}
      <GlowOrb $color="#6366f1" $top="10%" $left="20%" $size={300} $delay={0} />
      <GlowOrb $color="#22d3ee" $top="60%" $right="10%" $size={250} $delay={2} />
      <GlowOrb $color="#a855f7" $bottom="20%" $left="30%" $size={200} $delay={4} />
      
      {/* Grid Pattern */}
      <GridPattern />
      
      <HeroContent>
        {/* Slogan Section */}
        <SloganSection>
          <SloganBadge>
            <Zap size={14} />
            혁신의 시작점
          </SloganBadge>
          <SloganSub>{subSlogan}</SloganSub>
          <SloganMain>
            {mainSlogan.split('').map((char, i) => (
              <SloganChar key={i} $delay={i * 0.03}>
                {char === ' ' ? '\u00A0' : char}
              </SloganChar>
            ))}
          </SloganMain>
          <SloganDesc>
            투자 유치부터 포트폴리오 관리까지,<br />
            모든 흐름을 하나로 연결합니다.
          </SloganDesc>
        </SloganSection>
        
        {/* Feature Cards - Horizontal Layout */}
        {showNotificationCards && (
          <CardsContainer>
            <FeatureCard $delay={0.2} $accent="#3b82f6">
              <FeatureIconWrapper $color="#3b82f6">
                <Rocket size={24} />
              </FeatureIconWrapper>
              <FeatureLabel>Startup</FeatureLabel>
              <FeatureTitle>성장을 가속화</FeatureTitle>
              <FeatureDesc>투자 유치, IR 관리, 펀딩 트래킹을 한 곳에서</FeatureDesc>
              <FeatureStats>
                <StatItem>
                  <StatValue>2,500+</StatValue>
                  <StatLabel>스타트업</StatLabel>
                </StatItem>
              </FeatureStats>
            </FeatureCard>
            
            <FeatureCard $delay={0.35} $accent="#10b981" $featured>
              <FeaturedBadge>POPULAR</FeaturedBadge>
              <FeatureIconWrapper $color="#10b981">
                <TrendingUp size={24} />
              </FeatureIconWrapper>
              <FeatureLabel>Investor</FeatureLabel>
              <FeatureTitle>똑똑한 투자 관리</FeatureTitle>
              <FeatureDesc>딜 소싱부터 포트폴리오 성과 분석까지</FeatureDesc>
              <FeatureStats>
                <StatItem>
                  <StatValue>₩1.2T</StatValue>
                  <StatLabel>운용 자산</StatLabel>
                </StatItem>
              </FeatureStats>
            </FeatureCard>
            
            <FeatureCard $delay={0.5} $accent="#f59e0b">
              <FeatureIconWrapper $color="#f59e0b">
                <Lightbulb size={24} />
              </FeatureIconWrapper>
              <FeatureLabel>Accelerator</FeatureLabel>
              <FeatureTitle>프로그램 혁신</FeatureTitle>
              <FeatureDesc>참가자 관리와 멘토링 매칭을 효율적으로</FeatureDesc>
              <FeatureStats>
                <StatItem>
                  <StatValue>150+</StatValue>
                  <StatLabel>프로그램</StatLabel>
                </StatItem>
              </FeatureStats>
            </FeatureCard>
          </CardsContainer>
        )}
        
        {/* Floating Elements */}
        {showFloatingIcons && (
          <>
            <FloatingElement $top="8%" $left="5%" $delay={0} $size={56}>
              <Rocket size={24} />
            </FloatingElement>
            <FloatingElement $top="15%" $right="8%" $delay={1.5} $size={48}>
              <Target size={20} />
            </FloatingElement>
            <FloatingElement $top="65%" $left="3%" $delay={0.8} $size={44}>
              <BarChart3 size={18} />
            </FloatingElement>
            <FloatingElement $top="72%" $right="5%" $delay={2.2} $size={52}>
              <Users size={22} />
            </FloatingElement>
            
            {/* Small Decorative Dots */}
            <DecorativeDot $top="25%" $left="12%" $size={6} $delay={0.3} />
            <DecorativeDot $top="45%" $right="15%" $size={8} $delay={1.2} />
            <DecorativeDot $top="80%" $left="18%" $size={5} $delay={2.5} />
            <DecorativeDot $top="35%" $right="20%" $size={7} $delay={0.8} />
          </>
        )}
        
        {/* Bottom Stats Bar */}
        <BottomBar>
          <BottomBarItem>
            <BottomBarIcon><Clock size={14} /></BottomBarIcon>
            <BottomBarText>실시간 업데이트</BottomBarText>
          </BottomBarItem>
          <BottomBarDivider />
          <BottomBarItem>
            <BottomBarIcon><Zap size={14} /></BottomBarIcon>
            <BottomBarText>빠른 의사결정</BottomBarText>
          </BottomBarItem>
          <BottomBarDivider />
          <BottomBarItem>
            <BottomBarIcon><Target size={14} /></BottomBarIcon>
            <BottomBarText>목표 달성률 89%</BottomBarText>
          </BottomBarItem>
        </BottomBar>
      </HeroContent>
    </HeroContainer>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(3deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const dotPulse = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.5);
  }
`;

// ============================================
// Styled Components - Background
// ============================================

const HeroContainer = styled.div`
  position: relative;
  flex: 1;
  border-radius: 20px;
  overflow: hidden;
  height: 100%;
  min-height: 500px;
  
  @media (max-width: 1200px) {
    min-height: 450px;
  }
  
  @media (max-width: 768px) {
    min-height: 400px;
    border-radius: 16px;
  }
`;

const BgBase = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    #0f0f23 0%,
    #1a1a3e 25%,
    #0d1f3c 50%,
    #162033 75%,
    #0a0a1a 100%
  );
`;

const BgMesh = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.5;
  background: 
    radial-gradient(ellipse 80% 50% at 20% 40%, rgba(99, 102, 241, 0.15) 0%, transparent 50%),
    radial-gradient(ellipse 60% 60% at 80% 60%, rgba(34, 211, 238, 0.12) 0%, transparent 50%),
    radial-gradient(ellipse 50% 40% at 50% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%);
`;

const BgNoise = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.03;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
`;

const GlowOrb = styled.div<{ 
  $color: string; 
  $top?: string; 
  $left?: string; 
  $right?: string; 
  $bottom?: string; 
  $size: number;
  $delay: number;
}>`
  position: absolute;
  top: ${props => props.$top || 'auto'};
  left: ${props => props.$left || 'auto'};
  right: ${props => props.$right || 'auto'};
  bottom: ${props => props.$bottom || 'auto'};
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  background: radial-gradient(circle, ${props => props.$color}40 0%, transparent 70%);
  filter: blur(60px);
  animation: ${pulse} 8s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  @media (max-width: 1200px) {
    width: ${props => props.$size * 0.8}px;
    height: ${props => props.$size * 0.8}px;
    filter: blur(50px);
  }
  
  @media (max-width: 768px) {
    width: ${props => props.$size * 0.6}px;
    height: ${props => props.$size * 0.6}px;
    filter: blur(40px);
  }
`;

const GridPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 0%, transparent 70%);
`;

// ============================================
// Styled Components - Content
// ============================================

const HeroContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 3.5rem 2rem;
  z-index: 1;
  
  @media (max-width: 1200px) {
    padding: 2.5rem 1.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
    justify-content: center;
    gap: 1.5rem;
  }
`;

const SloganSection = styled.div`
  text-align: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const SloganBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(99, 102, 241, 0.25);
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #c7d2fe;
  margin-bottom: 1.25rem;
  animation: ${fadeIn} 0.6s ease-out;
  
  svg {
    color: #fbbf24;
  }
  
  @media (max-width: 1200px) {
    padding: 0.4rem 0.875rem;
    font-size: 0.7rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.65rem;
  }
`;

const SloganSub = styled.p`
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 0.75rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  animation: ${fadeIn} 0.8s ease-out 0.1s both;
  
  @media (max-width: 1200px) {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const SloganMain = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1rem;
  
  @media (max-width: 1200px) {
    font-size: 2.25rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;

const SloganChar = styled.span<{ $delay: number }>`
  display: inline-block;
  background: linear-gradient(135deg, #fff 0%, #e0e7ff 40%, #c7d2fe 60%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 0.5s ease-out ${props => props.$delay}s both;
`;

const SloganDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.7);
  animation: ${fadeIn} 0.8s ease-out 0.5s both;
  
  @media (max-width: 1200px) {
    font-size: 0.85rem;
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    
    br {
      display: none;
    }
  }
`;

// ============================================
// Styled Components - Feature Cards
// ============================================

const CardsContainer = styled.div`
  display: flex;
  gap: 1rem;
  width: 100%;
  max-width: 700px;
  perspective: 1000px;
  flex-shrink: 0;
  
  @media (max-width: 1200px) {
    max-width: 600px;
    gap: 0.75rem;
  }
  
  @media (max-width: 900px) {
    flex-direction: column;
    max-width: 320px;
    gap: 0.75rem;
  }
  
  @media (max-width: 768px) {
    max-width: 280px;
  }
`;

const FeatureCard = styled.div<{ $delay: number; $accent: string; $featured?: boolean }>`
  position: relative;
  flex: 1;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  backdrop-filter: blur(20px);
  animation: ${fadeInUp} 0.6s ease-out ${props => props.$delay}s both;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  
  ${props => props.$featured && `
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(16, 185, 129, 0.3);
    transform: scale(1.02);
  `}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${props => props.$accent}, transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px) ${props => props.$featured ? 'scale(1.02)' : 'scale(1)'};
    border-color: ${props => props.$accent}50;
    background: rgba(255, 255, 255, 0.05);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 60px ${props => props.$accent}15;
    
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 1200px) {
    padding: 1.25rem;
    border-radius: 16px;
  }
  
  @media (max-width: 900px) {
    ${props => props.$featured && `
      transform: scale(1);
    `}
    
    &:hover {
      transform: translateY(-4px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 14px;
  }
`;

const FeaturedBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.05em;
  
  @media (max-width: 1200px) {
    top: 10px;
    right: 10px;
    padding: 0.2rem 0.4rem;
    font-size: 0.55rem;
  }
  
  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    font-size: 0.5rem;
  }
`;

const FeatureIconWrapper = styled.div<{ $color: string }>`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, ${props => props.$color}20 0%, ${props => props.$color}10 100%);
  border: 1px solid ${props => props.$color}30;
  color: ${props => props.$color};
  
  svg {
    filter: drop-shadow(0 0 8px ${props => props.$color}60);
  }
  
  @media (max-width: 1200px) {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    margin-bottom: 0.75rem;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const FeatureLabel = styled.span`
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.375rem;
  
  @media (max-width: 1200px) {
    font-size: 0.65rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
    margin-bottom: 0.25rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
  
  @media (max-width: 1200px) {
    font-size: 1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.375rem;
  }
`;

const FeatureDesc = styled.p`
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1rem;
  
  @media (max-width: 1200px) {
    font-size: 0.75rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }
`;

const FeatureStats = styled.div`
  display: flex;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  
  @media (max-width: 1200px) {
    padding-top: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding-top: 0.5rem;
  }
`;

const StatItem = styled.div``;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(135deg, #fff 0%, #94a3b8 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 1200px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

// ============================================
// Styled Components - Floating Elements
// ============================================

const FloatingElement = styled.div<{ $top?: string; $left?: string; $right?: string; $delay: number; $size: number }>`
  position: absolute;
  top: ${props => props.$top || 'auto'};
  left: ${props => props.$left || 'auto'};
  right: ${props => props.$right || 'auto'};
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: rgba(255, 255, 255, 0.4);
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    color: rgba(255, 255, 255, 0.7);
    background: rgba(99, 102, 241, 0.1);
  }
  
  @media (max-width: 1200px) {
    width: ${props => props.$size * 0.85}px;
    height: ${props => props.$size * 0.85}px;
    border-radius: 12px;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const DecorativeDot = styled.div<{ $top?: string; $left?: string; $right?: string; $size: number; $delay: number }>`
  position: absolute;
  top: ${props => props.$top || 'auto'};
  left: ${props => props.$left || 'auto'};
  right: ${props => props.$right || 'auto'};
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  animation: ${dotPulse} 4s ease-in-out infinite;
  animation-delay: ${props => props.$delay}s;
  
  @media (max-width: 900px) {
    display: none;
  }
`;

// ============================================
// Styled Components - Bottom Bar
// ============================================

const BottomBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 100px;
  animation: ${fadeIn} 0.8s ease-out 0.8s both;
  flex-shrink: 0;
  
  @media (max-width: 1200px) {
    gap: 1rem;
    padding: 0.875rem 1.25rem;
  }
  
  @media (max-width: 900px) {
    flex-wrap: wrap;
    justify-content: center;
    border-radius: 16px;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    padding: 0.625rem 0.875rem;
  }
`;

const BottomBarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 900px) {
    gap: 0.375rem;
  }
`;

const BottomBarIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6366f1;
  
  @media (max-width: 900px) {
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const BottomBarText = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  
  @media (max-width: 1200px) {
    font-size: 0.7rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
  }
`;

const BottomBarDivider = styled.div`
  width: 1px;
  height: 16px;
  background: rgba(255, 255, 255, 0.1);
  
  @media (max-width: 1200px) {
    height: 14px;
  }
  
  @media (max-width: 900px) {
    display: none;
  }
`;

export default LoginHeroSection;
